import { useState, useEffect } from "react";
import { X, Sparkles, PartyPopper, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";

const NewYearPopup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem("newYearPopup2026");
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setOpen(true);
        sessionStorage.setItem("newYearPopup2026", "true");
        
        // Trigger confetti burst
        const duration = 3000;
        const end = Date.now() + duration;

        const frame = () => {
          confetti({
            particleCount: 3,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.6 },
            colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#FF69B4', '#32CD32'],
          });
          confetti({
            particleCount: 3,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.6 },
            colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#FF69B4', '#32CD32'],
          });

          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        };
        frame();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    // Celebration burst on close
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#FF69B4', '#32CD32', '#9370DB'],
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none">
        <div className="relative bg-gradient-to-br from-primary via-primary/95 to-primary/80 rounded-2xl p-8 text-primary-foreground overflow-hidden animate-in zoom-in-95 duration-500">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-4 left-4 w-20 h-20 bg-yellow-400/20 rounded-full blur-xl animate-pulse" />
            <div className="absolute bottom-4 right-4 w-32 h-32 bg-yellow-300/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
            
            {/* Floating sparkles */}
            <Sparkles className="absolute top-6 right-12 h-5 w-5 text-yellow-300/60 animate-bounce" style={{ animationDelay: '0.5s' }} />
            <Sparkles className="absolute bottom-12 left-8 h-4 w-4 text-yellow-200/50 animate-bounce" style={{ animationDelay: '1.2s' }} />
            <PartyPopper className="absolute top-12 left-12 h-6 w-6 text-yellow-300/40 animate-bounce" style={{ animationDelay: '0.8s' }} />
            <Star className="absolute bottom-8 right-10 h-5 w-5 text-yellow-200/50 animate-pulse" style={{ animationDelay: '0.3s' }} />
            <Star className="absolute top-8 right-6 h-4 w-4 text-yellow-300/60 animate-pulse" style={{ animationDelay: '1.5s' }} />
          </div>
          
          <DialogClose className="absolute right-3 top-3 z-10 rounded-full bg-white/10 backdrop-blur-sm p-2 opacity-70 ring-offset-background transition-all hover:opacity-100 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
          
          <div className="relative z-10 text-center space-y-5">
            <div className="flex justify-center gap-2">
              <Sparkles className="h-8 w-8 text-yellow-300 animate-pulse" />
              <PartyPopper className="h-8 w-8 text-yellow-200 animate-bounce" />
              <Sparkles className="h-8 w-8 text-yellow-300 animate-pulse" style={{ animationDelay: '0.5s' }} />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-3xl font-bold tracking-tight animate-in slide-in-from-bottom-4 duration-700">
                Happy New Year!
              </h2>
              <p className="text-6xl font-extrabold text-yellow-300 drop-shadow-lg animate-in zoom-in-50 duration-1000" style={{ animationDelay: '0.3s' }}>
                2026
              </p>
            </div>
            
            <p className="text-primary-foreground/90 text-sm leading-relaxed max-w-xs mx-auto animate-in fade-in duration-1000" style={{ animationDelay: '0.5s' }}>
              Wishing you prosperity, growth, and financial success in the new year. Thank you for being part of the Natcon SACCO family!
            </p>
            
            <Button 
              onClick={handleClose}
              className="bg-white text-primary hover:bg-white/90 font-semibold px-8 py-2 rounded-full shadow-lg transition-all hover:scale-105 animate-in slide-in-from-bottom-4 duration-700"
              style={{ animationDelay: '0.7s' }}
            >
              🎉 Let's Prosper Together 🎉
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewYearPopup;
